import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { ArticleFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { SettingsFormContainerProps } from "models/propTypes/articlePropTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelAccounting } from "wint-api-client/dist/types";
//#endregion
//#region components
import SettingsForm from "./settingsForm";
import { useInvoiceService, useArticleService, useAccountService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const SettingsFormContainer: ForwardRefRenderFunction<FormHandles, SettingsFormContainerProps> = (props, ref) => {
  const invoiceService = useInvoiceService();
  const articleService = useArticleService();
  const accountService = useAccountService();
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const { article, units } = props;

  const [revenueAccounts, setRevenueAccounts] =
    React.useState<WintSuperkollAPIModelAccounting.RevenueAccountsResponse>();

  React.useEffect(() => {
    accountService
      .getRevenueAccounts()
      .then(setRevenueAccounts)
      .catch(() => setRevenueAccounts({ enabled: false }));
  }, []);

  const validationSchema = Yup.object<ArticleFormTypes.Article>({
    name: Yup.string().required(TranslationKeys.Validations.Required),
    unitPrice: Yup.number().required(TranslationKeys.Validations.Required),
    vat: Yup.number(),
    vatText: Yup.string(),
    unit: Yup.string(),
    unitId: Yup.number(),
    euVatCategory: Yup.string(),
    euVatCategoryId: Yup.number(),
  });

  const getDefaultValues = (article?: WintSuperkollAPIModel.Article): ArticleFormTypes.Article => {
    // A non-VAT-registered company can only invoice VAT-free, so an older article with VAT is normalised to 0 on save.
    const vat = noVatCompany ? 0 : article?.vat;
    return {
      name: article?.text ?? "",
      unitPrice: article?.unitPrice,
      vat,
      vatText: vat === 0 ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree) : `${vat}%`,
      unitId: article?.unitId ? article.unitId : undefined,
      unit: article?.unitId ? units.find((u) => u.id === article.unitId)?.value : UtilsHelper.getSelectionNoneText(),
      euVatCategoryId: article?.euVatProductType,
      euVatCategory:
        article?.euVatProductType !== undefined
          ? invoiceService.getEuVatCategoryText(article.euVatProductType)
          : undefined,
      account: article?.account ? article.account : undefined,
      accountName: article?.account
        ? revenueAccounts?.accounts?.find((a) => a.number === article.account)?.name
        : undefined,
      active: !article?.inactive,
    };
  };

  const [defaultValues, setDefaultValues] = React.useState<ArticleFormTypes.Article>(getDefaultValues());

  React.useEffect(() => {
    // revenueAccounts is fetched in this container (above) and can still be undefined on first
    // mount, since SettingsFormContainer only mounts once `article` is already loaded by its
    // parent while the revenueAccounts fetch is still in flight. Re-running this when
    // revenueAccounts arrives re-resolves accountName instead of leaving it permanently blank.
    setDefaultValues(getDefaultValues(article));
  }, [article, revenueAccounts]);

  const handleSubmit = async (values: ArticleFormTypes.Article) => {
    return articleService
      .update(article.id, {
        text: values.name,
        unitPrice: values.unitPrice ?? 0,
        unitId: values.unitId,
        vat: values.vat ?? 0,
        euVatProductType: values.euVatCategoryId,
        inactive: !values.active,
        // account is tri-state server-side (omit/undefined = leave unchanged, 0 = explicitly clear, a
        // number = set it). The accountName field's onChangeHandler (settingsForm.tsx) clears
        // values.account on every VAT change regardless of the field's own `hidden` state, so when
        // the field is hidden here too (not opted in, or an agent-sale article) that clearing must
        // not reach the server as an explicit 0: for a not-opted-in company it would silently wipe a
        // stale account Core deliberately preserves, and for an agent-sale article it trips Core's
        // AgentArticleAccountLocked check. Only send the field's real value/clear when it was visible.
        account:
          revenueAccounts?.enabled === true && article?.isAgentSaleArticle !== true ? (values.account ?? 0) : undefined,
      })
      .then((res) => {
        setDefaultValues(values);
        return res.data;
      });
  };

  return (
    <SettingsForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
      units={units}
      canToggleActiveStatus={article?.canToggleActiveState}
      revenueAccounts={revenueAccounts}
      isAgentSaleArticle={article?.isAgentSaleArticle}
      noVatCompany={noVatCompany}
    />
  );
};

export default forwardRef(SettingsFormContainer);
